import React, { useState } from 'react';
import classNameBind from 'classnames/bind';
import { formatDate, formatToOptions, is12HourFormat } from '../../formatters';
import Popover from '../Popover';
import Button from '../Button';
import { useTranslation } from '../Translator';
import { fillWithZeros } from './TimeSelector/options';
import { PickerButton } from '../PickerButton';
import { TimeSelector } from './TimeSelector';
import styles from './TimePicker.module.css';
import { useBlur } from '../../hooks/useBlur';

const bindClasses = classNameBind.bind(styles);

const fromDateToTime = (date) => {
  if (!date) return null;

  return `${fillWithZeros(date.getHours())}:${fillWithZeros(date.getMinutes())}:${fillWithZeros(date.getSeconds())}`;
};

const fromTimeToDate = (time) => {
  if (!time) return null;

  const parts = time.split(':');
  const result = new Date();

  result.setHours(parts[0], parts[1], parts[2], 0);

  return result;
};

const TimePicker = ({
  value,
  name,
  onChange,
  onBlur,
  disabled,
  placeholder,
  dataId,
  error,
} = {}) => {
  const { t, i18n } = useTranslation('components');
  const [previousValue, setPreviousValue] = useState();
  const { ref, handleBlur } = useBlur({ name, value, onBlur, disabled });
  const dateTime = fromTimeToDate(value);
  const zeroStateText = placeholder || t('selectTime');
  const displayValue = value
    ? formatDate(dateTime, i18n.translator.language, formatToOptions('LT'))
    : null;

  const handleChange = (event) => {
    onChange({
      target: {
        name,
        value: fromDateToTime(event.target.value),
      },
    });
  };

  return (
    <div
      className={bindClasses('wrapper', { disabled })}
      onBlur={handleBlur}
      ref={ref}
      data-ppuc-id={dataId}
    >
      <Popover
        position="bottom"
        activatorElement={(
          <PickerButton
            icon={{ type: 'icon', content: 'time' }}
            dataId={dataId}
            disabled={disabled}
            invalid={!!error}
          >
            {displayValue || zeroStateText}
          </PickerButton>
        )}
        disabled={disabled}
        dataId={dataId}
        speechBubble
        noPadding
        onOpen={() => {
          setPreviousValue(value);
        }}
        onClose={() => {
          if (previousValue !== value) {
            onChange({
              target: {
                name,
                value: previousValue,
              },
            });
          }
        }}
      >
        {close => (
          <div className={bindClasses('picker-wrapper')}>
            <TimeSelector
              is12HourFormat={is12HourFormat(i18n.translator.language)}
              dataId={`${dataId}.options`}
              value={dateTime}
              onChange={handleChange}
              fixedHeight
            />
            <div className={bindClasses('apply-wrapper')}>
              <Button
                text={t('apply')}
                fullWidth
                appearance="primary"
                disabled={previousValue === value}
                onClick={() => {
                  setPreviousValue(value);
                  close();
                }}
              />
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
};

TimePicker.defaultProps = {
  dataId: 'TimePicker',
};

export {
  TimePicker,
};
